/**
 * A playback session over the sources of a timeline window (design §7.2):
 * one master clock, a video chain per source with video, and each audio file
 * decoded near the playhead. Call tick() once per animation frame.
 *
 * @module
 */
import type { AgcPreferences, ProxyTiming } from "../api/client";
import { createAgcNode, type AgcNode } from "../audio/agc";
import { Mixer } from "../audio/mixer";
import { VideoChain, type ProxySource } from "./chain";
import { PlaybackController } from "./controller";
import { apiFull, FullResolution, type FullApi } from "./full";
import { LazyAudio } from "./lazy-audio";
import { fileAt, type SourcePlan } from "./plan";
import { audioContextTimeSource, AudioWatch, PlaybackClock } from "./sync";

/** H.264 Constrained Baseline 3.1 (openh264, design §6.6); a video's sound has its own proxy. */
const VIDEO_MIME = 'video/mp4; codecs="avc1.42c01f"';

/** Whether this browser plays the working copies (H.264 video, AAC sound). */
export function playsWorkingCopies(): boolean {
  return (
    typeof MediaSource !== "undefined" &&
    MediaSource.isTypeSupported(VIDEO_MIME) &&
    MediaSource.isTypeSupported('audio/mp4; codecs="mp4a.40.2"')
  );
}
/** Full-resolution proxies are up to 1080p: level 4.0. */
const FULL_VIDEO_MIME = 'video/mp4; codecs="avc1.42c028"';
const FULL_POLL_MS = 3000;
/** Decode audio this long before it starts... */
const AUDIO_LOAD_BEFORE_MS = 20_000;
/** ...and free it this long after the playhead leaves it. */
const AUDIO_KEEP_AFTER_MS = 30_000;

const AUDIO_CHECK_MS = 1000;

export const RATES = [0.25, 0.5, 1, 2, 4, 8] as const;

export interface SessionSource {
  plan: SourcePlan;
  chain: VideoChain | null;
}

/** The file at the playhead of the source shown at full resolution. */
export interface FullState {
  key: string;
  /** Playing from its full-resolution proxy; else from the normal one while it is made. */
  showing: boolean;
  error: string | null;
}

interface Full {
  key: string;
  res: FullResolution;
  /** Files the current chain plays from their full proxies. */
  shown: ReadonlySet<string>;
  timer: ReturnType<typeof setInterval>;
  error: string | null;
}

export class PlaybackSession {
  readonly ctx = new AudioContext({ latencyHint: "playback" });
  /** Per-source volume, mute and solo, keyed by SourcePlan.key. */
  readonly mixer = new Mixer(this.ctx);
  readonly clock: PlaybackClock;
  readonly controller: PlaybackController;
  readonly sources: SessionSource[];
  private readonly audio: LazyAudio[];
  /** Null until loaded, or if the worklet can't run here. */
  private agc: AgcNode | null = null;
  /** The master limiter while the AGC is off: native, so no worklet thread runs. */
  private readonly limiter: DynamicsCompressorNode;
  private agcError: string | null = null;
  /** Why the clock left the AudioContext for wall time, if it has. */
  private audioLost: "never-ran" | "stopped" | null = null;
  private readonly audioWatch = new AudioWatch();
  private readonly audioTimer = setInterval(() => this.checkAudio(), AUDIO_CHECK_MS);
  private agcPrefs: AgcPreferences = { enabled: false, preset: "speech", target_dbfs: -20 };
  private readonly proxies: ProxySource;
  private readonly fullApi: FullApi;
  private full: Full | null = null;

  private constructor(plans: readonly SourcePlan[], startUtcMs: number, proxies: ProxySource, fullApi: FullApi) {
    this.limiter = brickwall(this.ctx);
    this.proxies = proxies;
    this.fullApi = fullApi;
    this.clock = new PlaybackClock(startUtcMs, audioContextTimeSource(this.ctx));
    this.sources = plans.map((p) => {
      const plan = { ...p, video: [...p.video], audio: [...p.audio] };
      return { plan, chain: this.chainFor(plan) };
    });
    this.audio = plans.flatMap((p) => p.audio.map((f) => new LazyAudio(this.ctx, this.mixer.input(p.key), f, proxies)));
    this.controller = new PlaybackController(
      this.clock,
      [...this.sources.flatMap((s) => (s.chain ? [s.chain.track] : [])), ...this.audio.map((a) => a.track)],
      { correction: correctionFor(navigator.userAgent) },
    );
  }

  static create(
    plans: readonly SourcePlan[],
    startUtcMs: number,
    proxies: ProxySource = apiProxies(),
    fullApi: FullApi = apiFull(),
  ): Promise<PlaybackSession> {
    const session = new PlaybackSession(plans, startUtcMs, proxies, fullApi);
    return createAgcNode(session.ctx).then(
      (agc) => {
        session.agc = agc;
        session.setAgc(session.agcPrefs);
        return session;
      },
      (e: unknown) => {
        session.agcError = String(e);
        return session;
      },
    );
  }

  /**
   * The playback AGC on the mix (design §7.4.1). Off, a native limiter takes
   * its place: the worklet kept a real-time thread 18% busy on the Android
   * tablet. Both look ahead ~5-6 ms, so switching barely shifts the audio.
   */
  setAgc(prefs: AgcPreferences): void {
    this.agcPrefs = prefs;
    const agc = prefs.enabled ? this.agc : null;
    agc?.setPreset(prefs.preset);
    agc?.setTarget(prefs.target_dbfs);
    this.mixer.setProcessor(agc?.node ?? this.limiter);
  }

  /** Gain the AGC is applying now (dB), or null if it is off or unavailable. */
  get agcGainDb(): number | null {
    return this.agc && this.agcPrefs.enabled ? this.agc.gainDb : null;
  }

  /**
   * Adds files that start after what each source already has, such as the
   * next day's, without interrupting playback.
   */
  extend(plans: readonly SourcePlan[]): void {
    for (const p of plans) {
      let s = this.sources.find((x) => x.plan.key === p.key);
      if (!s) {
        s = { plan: { ...p, video: [], audio: [] }, chain: null };
        this.sources.push(s);
      }
      const known = new Set([...s.plan.video, ...s.plan.audio].map((f) => f.id));
      const video = p.video.filter((f) => !known.has(f.id));
      const audio = p.audio.filter((f) => !known.has(f.id));
      s.plan.video.push(...video);
      s.plan.audio.push(...audio);
      if (s.chain) s.chain.addFiles(video);
      else if (video.length > 0) {
        s.chain = this.chainFor({ ...s.plan, video });
        if (s.chain) this.controller.addTrack(s.chain.track);
      }
      for (const f of audio) this.addAudio(new LazyAudio(this.ctx, this.mixer.input(p.key), f, this.proxies));
    }
  }

  private chainFor(plan: SourcePlan): VideoChain | null {
    return plan.video.length > 0 ? new VideoChain(plan.video, this.proxies, VIDEO_MIME) : null;
  }

  private addAudio(a: LazyAudio): void {
    this.audio.push(a);
    this.controller.addTrack(a.track);
  }

  /**
   * Plays one source from full-resolution proxies, made on demand; null
   * returns it to the normal ones (design §7.3).
   */
  setFullResolution(key: string | null): void {
    if (this.full?.key === key) return;
    if (this.full) {
      const { key: was, timer } = this.full;
      clearInterval(timer);
      this.full = null;
      const s = this.sources.find((x) => x.plan.key === was);
      if (s) this.rebuild(s, this.proxies, VIDEO_MIME);
    }
    const s = this.sources.find((x) => x.plan.key === key);
    if (key === null || !s?.chain) return;
    const res = new FullResolution(this.fullApi);
    const full: Full = { key, res, shown: new Set(), timer: setInterval(() => check(), FULL_POLL_MS), error: null };
    const check = () =>
      void res.poll(s.plan.video, this.clock.now()).then(
        () => {
          full.error = null;
          if (this.full !== full) return;
          const f = s.plan.video[fileAt(s.plan.video, this.clock.now())];
          if (f && res.isReady(f.id) && !full.shown.has(f.id)) {
            full.shown = res.readyIds;
            this.rebuild(s, res.proxies(this.proxies, full.shown), FULL_VIDEO_MIME);
          }
        },
        (e: unknown) => {
          full.error = String(e);
        },
      );
    this.full = full;
    check();
  }

  get fullResolution(): FullState | null {
    const full = this.full;
    const s = full && this.sources.find((x) => x.plan.key === full.key);
    if (!full || !s) return null;
    const f = s.plan.video[fileAt(s.plan.video, this.clock.now())];
    return {
      key: full.key,
      showing: f !== undefined && full.shown.has(f.id),
      error: (f && full.res.error(f.id)) ?? full.error,
    };
  }

  /** Replaces a source's chain, e.g. to switch its proxies; it reloads from the playhead. */
  private rebuild(s: SessionSource, proxies: ProxySource, mime: string): void {
    const old = s.chain;
    if (!old) return;
    const chain = new VideoChain(s.plan.video, proxies, mime);
    chain.track.suspended = old.track.suspended;
    old.close();
    this.controller.replaceTrack(old.track, chain.track);
    s.chain = chain;
  }

  /** Stops decoding the video of sources not in `shown`. */
  showOnly(shown: ReadonlySet<string>): void {
    for (const s of this.sources) if (s.chain) s.chain.track.suspended = !shown.has(s.plan.key);
  }

  get isPlaying(): boolean {
    return this.controller.isPlaying;
  }

  get isHeld(): boolean {
    return this.controller.isHeld;
  }

  now(): number {
    return this.clock.now();
  }

  tick(): void {
    const now = this.clock.now();
    for (const s of this.sources) if (!s.chain?.track.suspended) s.chain?.want(now);
    for (const a of this.audio) {
      const f = a.file;
      if (now >= f.fromUtcMs - AUDIO_LOAD_BEFORE_MS && now < f.toUtcMs) a.load();
      else if (now < f.fromUtcMs - 2 * AUDIO_LOAD_BEFORE_MS || now >= f.toUtcMs + AUDIO_KEEP_AFTER_MS) a.unload();
    }
    this.controller.tick();
  }

  /** Needs a user gesture: it starts the AudioContext. */
  play(): void {
    void this.ctx.resume().catch(() => undefined);
    this.controller.play();
  }

  /**
   * With no working output (no device, a stalled audio stack, a headset
   * unplugged) the context stops or crawls, and the clock it drives with it:
   * hand the clock to wall time.
   */
  private checkAudio(): void {
    if (this.audioLost || !this.controller.isPlaying || this.ctx.state === "closed") {
      this.audioWatch.pause();
      return;
    }
    const r = this.audioWatch.check(this.ctx.currentTime, performance.now());
    if (r !== "ok") {
      this.audioLost = r;
      this.clock.setSource(() => performance.now());
    }
  }

  pause(): void {
    this.controller.pause();
  }

  seek(utcMs: number): void {
    this.controller.seek(utcMs);
    this.tick();
  }

  setRate(rate: number): void {
    this.controller.setRate(rate);
  }

  get rate(): number {
    return this.clock.playbackRate;
  }

  /** Whether a source has a recording at the playhead. */
  isActive(s: SessionSource): boolean {
    const now = this.clock.now();
    return (
      s.chain?.track.coverage?.some((c) => c.fromUtcMs <= now && now < c.toUtcMs) ??
      s.plan.audio.some((f) => f.fromUtcMs <= now && now < f.toUtcMs)
    );
  }

  errors(): string[] {
    return [
      ...this.sources.flatMap((s) => (s.chain?.error ? [`${s.plan.source}: ${s.chain.error}`] : [])),
      ...this.audio.flatMap((a) => (a.error ? [a.error] : [])),
      ...(this.agcError && this.agcPrefs.enabled ? [`AGC unavailable: ${this.agcError}`] : []),
      ...(this.audioLost === "never-ran" ? ["No audio output: playing without sound"] : []),
      ...(this.audioLost === "stopped" ? ["Audio output stopped: playing on without sound"] : []),
    ];
  }

  destroy(): void {
    clearInterval(this.audioTimer);
    if (this.full) clearInterval(this.full.timer);
    this.full = null;
    this.controller.pause();
    for (const s of this.sources) s.chain?.close();
    for (const a of this.audio) a.unload();
    void this.ctx.close();
  }
}

/**
 * Android Chrome takes 0.2-0.8 s to seek, so seek corrections stall the
 * video every time; it honours playbackRate on MSE, which WebKitGTK doesn't.
 */
export function correctionFor(userAgent: string): "rate" | "seek" {
  return userAgent.includes("Android") ? "rate" : "seek";
}

function brickwall(ctx: BaseAudioContext): DynamicsCompressorNode {
  return new DynamicsCompressorNode(ctx, { threshold: -1, knee: 0, ratio: 20, attack: 0.001, release: 0.05 });
}

/** Proxies from the API, each file's timing fetched once. */
export function apiProxies(): ProxySource {
  const timings = new Map<string, Promise<ProxyTiming>>();
  return {
    url: (id) => `/api/files/${id}/proxy`,
    audioUrl: (id) => `/api/files/${id}/audio`,
    timing: (id) => {
      let t = timings.get(id);
      if (!t) {
        t = fetch(`/api/files/${id}/proxy/timing`, {
          credentials: "same-origin",
        }).then((r) => {
          if (!r.ok) throw new Error(`proxy timing: ${r.status}`);
          return r.json() as Promise<ProxyTiming>;
        });
        t.catch(() => timings.delete(id));
        timings.set(id, t);
      }
      return t;
    },
  };
}
